//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import VendorDuesModel from './vendorDues.model';

/**
 * @service VendorDuesService
 * @description Service for managing vendor dues and outstanding payment obligations within the Material Management System (MMS).
 * This service allows the application to track how much is owed to vendors for materials or services purchased.
 *
 * It interacts with the backend to retrieve dues summaries and detailed records, facilitating financial planning and payment scheduling.
 *
 * **Key Responsibilities:**
 * 1. **Dues Retrieval**: Fetching outstanding dues filtered by business and farm site.
 * 2. **Item-Specific Dues**: Retrieving dues associated with specific items.
 * 3. **Role-Based Security**: Adjusting query parameters and payloads based on the user's role (Employee vs. others) to ensure data isolation.
 * 4. **Resource Management**: Handling HTTP request lifecycles to prevent memory leaks.
 *
 * **Data Flow:**
 * - **List**: Component calls `getVendorDuesByBusinessId`. Service checks role. Service calls `/mms/vendorpaymentdues/`.
 * - **Item Detail**: Component calls `getVendorsByBusinessIdItemId`. Service sanitizes payload. Service posts to `/mms/vendordues/businessID/itemId`.
 *
 * @feature 1. View all vendor dues for a farm site.
 * @feature 2. Filter dues by specific items.
 * @feature 3. Role-based access control for financial data.
 *
 * @architecture
 * Extends base Service<VendorDuesModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /mms/vendordues/ (Base), /mms/vendorpaymentdues/
 * @service-provider root (singleton, application-wide)
 *
 * @local-storage-keys
 * - loginPersonType: Used to determine if the user is an 'EMPLOYEE', affecting query construction and payload sanitization.
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class VendorDuesService extends Service<VendorDuesModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/mms/vendordues/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/mms/vendordues/');
  }

  /**
   * @method getVendorDuesByBusinessId
   * @async
   * @description Fetch vendor dues associated with a specific business and farm site.
   *
   * This method retrieves a list of outstanding payments. It targets the `/mms/vendorpaymentdues/` endpoint directly,
   * overriding the base service URL.
   *
   * **Role-Based Logic:**
   * - If `loginPersonType` is 'EMPLOYEE', the `business_id` query parameter is included.
   * - Otherwise, it is omitted, relying on the backend to infer context.
   *
   * @param {string} businessId - The unique identifier of the business.
   * @param {string} farmSiteId - The unique identifier of the farm site.
   * @returns {Promise<Array<VendorDuesModel>>} Promise resolving to an array of VendorDuesModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request to `/mms/vendorpaymentdues/`.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * vendorDuesService.getVendorDuesByBusinessId('bus-1', 'site-2').then(dues => {
   *   console.log('Outstanding Dues:', dues);
   * });
   */
  public getVendorDuesByBusinessId(businessId: string, farmSiteId: string) {
    let queryPath = '';
    if (localStorage.loginPersonType == 'EMPLOYEE') {
      queryPath = `business_id=${businessId}&`;
    } else {
      queryPath = ``;
    }
    return new Promise<Array<VendorDuesModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<VendorDuesModel>>(`${this.baseUrl}/mms/vendorpaymentdues/?${queryPath}farm_site_id=${farmSiteId}&order=new&orderWithUpdatedAt=true&page=0&pageSize=20000`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getVendorsByBusinessIdItemId
   * @async
   * @description Fetch vendor dues specific to a particular item.
   *
   * This allows users to see how much is owed for a specific type of material (e.g., "How much do we owe for Feed X?").
   *
   * **Security Logic:**
   * - Removes `businessId` from the payload if the user is not an 'EMPLOYEE'.
   *
   * @param {string} businessId - The unique identifier of the business.
   * @param {string} farmSiteId - The unique identifier of the farm site.
   * @param {string} itemId - The unique identifier of the item.
   * @returns {Promise<Array<VendorDuesModel>>} Promise resolving to the list of dues for that item.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `businessID/itemId`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getVendorsByBusinessIdItemId(businessId: string, farmSiteId: string, itemId: string) {
    return new Promise<Array<VendorDuesModel>>((resolve, reject) => {
      let payload = { businessId, farmSiteId, itemId}
      if (localStorage.loginPersonType != 'EMPLOYEE') {
        delete payload.businessId;
      }
      const request = this.httpClient.post<Array<VendorDuesModel>>(`${this.baseUrl}${this.serviceUrl}businessID/itemId`, payload, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
